import { useWavesurfer } from '@wavesurfer/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { useRemoveSectionAudioMutation } from 'services/api/upload-vehicle';
import Tooltip from 'components/Tooltip';
import { PlayIcon, PauseIcon } from 'pages/CreateVehicle/icons/AudioIcons';
import { toast } from 'react-hot-toast';
import { useIsOnline } from 'hooks';
import { generateRandomId } from 'utils/crypto';
import { db } from 'db';

type Props = {
  sectionId: string;
  size?: 'lg' | 'sm';
  autosave?: boolean;
};

const Container = styled.div<{ removeHovered?: boolean; size?: 'lg' | 'sm' }>`
  height: ${props => (props.size === 'lg' ? '50px' : '44px')};
  width: 100%;
  align-items: center;
  display: flex;
  border: ${props => (props.removeHovered ? '1px solid rgba(175, 41, 48, 1)' : '1px solid rgba(204, 204, 204, 0.8)')};
  border-radius: 8px;
  gap: 8px;
  padding: 0px 8px;
  background: ${props => (props.removeHovered ? 'rgba(175, 41, 48, 1)' : 'transparent')};
`;

const IconWrapper = styled.div`
  cursor: pointer;
  display: flex;
`;

const PlayPauseIcon = styled.div<{ removeHovered?: boolean }>`
  border-radius: 100%;
  width: 32px;
  height: 32px;
  background: ${props => (props.removeHovered ? 'rgba(226, 110, 116, 1)' : '#666666')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimeContainer = styled.div<{ removeHovered?: boolean; size: 'sm' | 'lg' }>`
  border-radius: 8px;
  background: ${props => (props.removeHovered ? 'rgba(226, 110, 116, 1)' : 'rgba(204, 204, 204, 0.8)')};
  color: ${props => (props.removeHovered ? '#fff' : 'rgba(102, 102, 102, 1)')};
  font-weight: 700;
  font-size: ${props => (props.size === 'lg' ? '14px' : '10px')};
  line-height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => (props.size === 'lg' ? '52px' : '35px')};
  height: 24px;
`;

const formatTime = (seconds: number) => [seconds / 60, seconds % 60].map(v => `0${Math.floor(v)}`.slice(-2)).join(':');

let globalAudioContext: AudioContext | null = null;

const getOrCreateAudioContext = () => {
  if (!globalAudioContext) {
    globalAudioContext = new window.AudioContext();
  }
  return globalAudioContext;
};

export const SoundPlayer = ({ sectionId, size = 'lg', autosave = true }: Props) => {
  const [removeSectionAudio] = useRemoveSectionAudioMutation();
  const {
    input: { value: vehicleId }
  } = useField('vehicleId');

  const containerRef = useRef<any>(null);
  const [removeHovered, setRemoveHovered] = useState(false);

  const {
    input: { value: numericSectionId }
  } = useField(`${sectionId}.id`);

  const onRemoveHovered = () => setRemoveHovered(true);
  const onRemoveHoveredOut = () => setRemoveHovered(false);

  const { input } = useField(`${sectionId}.audio_url`);
  const {
    input: { value: audioBlob }
  } = useField(`${sectionId}.audio_file`);

  const [displayUrl, setDisplayUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (audioBlob) {
      const objectUrl = URL.createObjectURL(audioBlob);
      setDisplayUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    } else if (input.value) {
      setDisplayUrl(input.value);
    } else {
      setDisplayUrl(undefined);
    }
  }, [audioBlob, input.value]);

  const { wavesurfer, isPlaying, currentTime, ...rest } = useWavesurfer({
    container: containerRef,
    height: size === 'lg' ? 50 : 34,
    fillParent: true,
    barWidth: 1,
    barHeight: 1,
    barGap: 2,
    waveColor: removeHovered ? '#fff' : 'rgba(204, 204, 204, 1)',
    progressColor: removeHovered ? '#fff' : 'rgba(71, 151, 71, 1)',
    cursorWidth: 0,
    url: displayUrl,
    backend: 'WebAudio'
  });

  const isOnline = useIsOnline();

  const handleRemove = async () => {
    if (autosave) {
      if (!isOnline) {
        await db.entityUpdates.add({
          id: generateRandomId(),
          entityId: numericSectionId,
          entityType: 'section',
          formId: vehicleId,
          timestamp: Date.now(),
          synced: false,
          key: 'audio',
          value: ''
        });
        input.onChange(null);
        return;
      }
      await removeSectionAudio({ vehicleId, sectionId: numericSectionId });
      toast.success('Audio removed');
    }
    input.onChange(null);
  };

  const onPlayPause = useCallback(async () => {
    if (!wavesurfer) return;
    // try {
    //   const mediaElement = wavesurfer.getMediaElement();
    //   console.log(mediaElement, 'mediaElement');
    //   if (!mediaElement) return;
    //   // @ts-expect-error
    //   const ctx = mediaElement.audioContext;
    //   console.log(ctx, 'audio context');
    //   if (ctx && ctx.state === 'suspended') {
    //     await ctx.resume();
    //   }
    // } catch (error) {
    //   console.error('Error resuming audio context:', error);
    // }

    // const mediaElement = wavesurfer.getMediaElement();
    // console.log(mediaElement, 'mediaElement2');
    // if (mediaElement && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
    //   // iOS Safari: ensure the media is fully loaded
    //   if (mediaElement.readyState < 3) {
    //     // HAVE_FUTURE_DATA
    //     console.log('Waiting for media to load, readyState:', mediaElement.readyState);
    //     await new Promise(resolve => {
    //       const onCanPlay = () => {
    //         mediaElement.removeEventListener('canplay', onCanPlay);
    //         resolve(null);
    //       };
    //       mediaElement.addEventListener('canplay', onCanPlay);

    //       // Also try loading it
    //       mediaElement.load();

    //       // Timeout fallback
    //       setTimeout(() => {
    //         mediaElement.removeEventListener('canplay', onCanPlay);
    //         resolve(null);
    //       }, 2000);
    //     });
    //   }

    //   console.log('Ready to play, readyState:', mediaElement.readyState);
    // }
    

    wavesurfer.playPause();
  }, [wavesurfer]);

  const duration = wavesurfer?.getDuration() || 0;

  useEffect(() => {
    console.log(window.AudioContext, 'window.AudioContext');
    console.log((window as any).webkitAudioContext, 'webkitAudioContext');
    console.log((wavesurfer as any)?.backend, 'wave back');
    // if (wavesurfer && displayUrl) {
    //   // Resume AudioContext on iOS Safari after microphone is released
    //   const resumeAudioContext = async () => {
    //     if (wavesurfer.backend?.ac?.state === 'suspended') {
    //       await wavesurfer.backend.ac.resume();
    //     }
    //   };

    //   // Small delay to ensure microphone tracks are fully released
    //   setTimeout(resumeAudioContext, 100);
    // }
  }, [wavesurfer, displayUrl]);

  return (
    <Container size={size} removeHovered={removeHovered}>
      <PlayPauseIcon removeHovered={removeHovered} onClick={onPlayPause}>
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </PlayPauseIcon>
      <div style={{ flexGrow: 1 }} ref={containerRef} />
      <TimeContainer size={size} removeHovered={removeHovered}>
        {formatTime(isPlaying ? currentTime : duration)}
      </TimeContainer>
      <Tooltip title="Remove" titleStyle="uploadVehicle" isTextOverflown>
        <IconWrapper onMouseOver={onRemoveHovered} onMouseOut={onRemoveHoveredOut} onClick={handleRemove}>
          <BinIcon color={removeHovered ? '#fff' : '#999999'} />
        </IconWrapper>
      </Tooltip>
    </Container>
  );
};

const BinIcon = ({ color }: { color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z"
      fill={color || '#999999'}
    />
  </svg>
);

